import { httpClient } from './http-client.service';
import type {
  SandboxDatasetAPI,
  SandboxJourneysResponse,
  SandboxRunAPI,
  SandboxRunItemAPI,
  TriggerRunRequest,
} from '../types/analytics-sandbox';

export const analyticsSandboxAPI = {
  // ── Datasets ──────────────────────────────────────────
  getDatasets: async (datasetType: 'SANDBOX' | 'PDF_CONSOLE' = 'SANDBOX'): Promise<SandboxDatasetAPI[]> => {
    const response = await httpClient.get<any>(`sandbox/datasets?dataset_type=${datasetType}`);
    return response.data?.data || response.data || [];
  },

  createDataset: async (name: string, description?: string, datasetType?: 'SANDBOX' | 'PDF_CONSOLE'): Promise<SandboxDatasetAPI> => {
    const response = await httpClient.post<any>('sandbox/datasets', { name, description, dataset_type: datasetType });
    return response.data?.data || response.data;
  },

  getDataset: async (datasetId: string): Promise<SandboxDatasetAPI> => {
    const response = await httpClient.get<any>(`sandbox/datasets/${datasetId}`);
    return response.data?.data || response.data;
  },

  deleteDataset: async (datasetId: string): Promise<void> => {
    await httpClient.delete<any>(`sandbox/datasets/${datasetId}`);
  },

  uploadFile: async (datasetId: string, file: File, password?: string): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    if (password) formData.append('password', password);
    // Delete Content-Type so the browser sets multipart/form-data with the correct boundary.
    // Without this, the httpClient default 'application/json' header overrides it and multer
    // can't parse the multipart body ("No file uploaded").
    await httpClient.post<any>(`sandbox/datasets/${datasetId}/files`, formData, {
      transformRequest: [(data: any, headers: any) => {
        if (headers) delete headers['Content-Type'];
        return data;
      }],
    });
  },

  deleteFile: async (datasetId: string, fileName: string): Promise<void> => {
    await httpClient.delete<any>(`sandbox/datasets/${datasetId}/files/${encodeURIComponent(fileName)}`);
  },

  addConsents: async (datasetId: string, consentIds: string[]): Promise<{ added: number; duplicates: number }> => {
    const response = await httpClient.post<any>(`sandbox/datasets/${datasetId}/consents`, { consent_ids: consentIds });
    return response.data?.data || response.data;
  },

  removeConsent: async (datasetId: string, consentId: string): Promise<void> => {
    await httpClient.delete<any>(`sandbox/datasets/${datasetId}/consents/${encodeURIComponent(consentId)}`);
  },

  // ── Journeys ──────────────────────────────────────────
  getJourneys: async (): Promise<SandboxJourneysResponse> => {
    const response = await httpClient.get<any>('sandbox/journeys');
    const data = response.data?.data || response.data || {};
    // Handle both old flat-array format and new { pdf, consent } format
    if (Array.isArray(data)) {
      return { pdf: data, consent: [] };
    }
    return {
      pdf: data.pdf || [],
      consent: data.consent || [],
      limits: data.limits,
      zip_download_enabled: data.zip_download_enabled,
    };
  },

  // ── Runs ──────────────────────────────────────────────
  getRuns: async (params?: { datasetId?: string; status?: string; transactionRef?: string; page?: number; limit?: number }): Promise<{ runs: SandboxRunAPI[]; total: number }> => {
    const query = new URLSearchParams();
    if (params?.datasetId) query.set('dataset_id', params.datasetId);
    if (params?.status) query.set('status', params.status);
    if (params?.transactionRef) query.set('transaction_ref', params.transactionRef);
    if (params?.page != null) query.set('page', String(params.page));
    if (params?.limit != null) query.set('limit', String(params.limit));
    const qs = query.toString();
    const response = await httpClient.get<any>(`sandbox/runs${qs ? `?${qs}` : ''}`);
    const payload = response.data?.data ?? response.data;
    if (Array.isArray(payload)) return { runs: payload, total: payload.length };
    return { runs: payload?.runs ?? payload?.items ?? payload?.data ?? [], total: payload?.total ?? 0 };
  },

  triggerRun: async (request: TriggerRunRequest): Promise<SandboxRunAPI> => {
    const response = await httpClient.post<any>('sandbox/runs', request);
    return response.data?.data || response.data;
  },

  getRunResults: async (runId: string, page: number = 1, limit: number = 50): Promise<{ items: SandboxRunItemAPI[]; total: number }> => {
    const response = await httpClient.get<any>(`sandbox/runs/${runId}/results?page=${page}&limit=${limit}`);
    const payload = response.data?.data ?? response.data;
    if (Array.isArray(payload)) return { items: payload, total: payload.length };
    return { items: payload?.items ?? payload?.data ?? [], total: payload?.total ?? 0 };
  },

  downloadRunResults: async (runId: string): Promise<{ job_id: string; status: string }> => {
    const response = await httpClient.get<any>(`sandbox/runs/${runId}/download`);
    return response.data?.data || response.data;
  },

  getDownloadJobStatus: async (jobId: string): Promise<{ status: string; download_url?: string; error?: string }> => {
    const response = await httpClient.get<any>(`downloads/status/${jobId}`);
    return response.data?.data || response.data;
  },

  getJobDownloadUrl: async (jobId: string): Promise<string> => {
    const response = await httpClient.get<any>(`downloads/${jobId}/url`);
    const data = response.data?.data || response.data;
    return data?.download_url || '';
  },
};
